import axios from 'axios';

import { logout } from '../store/slices/authSlice';
import { translateAxiosError } from '../utils/errorTranslator';

// Store reference for logout - will be set by store initialization
let store = null;

export const setApiStore = storeInstance => {
  store = storeInstance;
};

// Create axios instance
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  timeout: 0, // No timeout (unlimited)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and refresh if needed
api.interceptors.request.use(
  async config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Check if token needs refresh before making request
      const validToken = await refreshTokenIfNeeded();
      config.headers.Authorization = `Bearer ${validToken}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Track ongoing refresh requests to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to check if token is expired or about to expire
const isTokenExpiredOrExpiringSoon = token => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = exp - now;

    // Consider token expired if it expires within 5 minutes
    return timeUntilExpiry < 5 * 60 * 1000;
  } catch (error) {
    console.warn('Error parsing token:', error);
    return true;
  }
};

// Helper function to handle logout
const handleLogout = () => {
  // Clear tokens
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');

  // Dispatch Redux logout action if store is available
  if (store) {
    store.dispatch(logout());
  }
  window.location.href = '/';
};

// Proactive token refresh
const refreshTokenIfNeeded = async () => {
  const token = localStorage.getItem('access_token');

  if (isTokenExpiredOrExpiringSoon(token)) {
    const refreshToken = localStorage.getItem('refresh_token');

    if (refreshToken && !isRefreshing) {
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/refresh`,
          { refresh_token: refreshToken }
        );

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        return access_token;
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
        handleLogout();
        throw error;
      } finally {
        isRefreshing = false;
      }
    }
  }

  return token;
};

// Response interceptor for error handling
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          api.defaults.headers.common['Authorization'] =
            `Bearer ${access_token}`;

          // Process queued requests
          processQueue(null, access_token);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } else {
          throw new Error('No refresh token available');
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other 401 errors (after retry or when refresh is not available)
    if (error.response?.status === 401 && originalRequest._retry) {
      // Token refresh already failed, logout user
      console.error('Authentication failed after token refresh attempt');
      handleLogout();
    }

    // Translate error message before rejecting
    const translatedError = {
      ...error,
      message: translateAxiosError(error),
    };
    return Promise.reject(translatedError);
  }
);

// API endpoints
export const endpoints = {
  // Auth
  login: '/auth/login',
  signup: '/auth/signup',
  refresh: '/auth/refresh',

  // Users
  userMe: '/users/me',
  adminUsers: '/admin/users',
  adminUser: userId => `/admin/users/${userId}`,
  lockUser: userId => `/admin/users/${userId}/lock`,
  unlockUser: userId => `/admin/users/${userId}/unlock`,

  // Companies
  company: id => `/admin/companies/${id}`,
  companies: '/companies',
  publicCompanies: '/companies/public', // For signup - no auth required
  adminCompanies: '/admin/companies',
  adminCompany: companyId => `/admin/companies/${companyId}`,

  // PDFs
  companyPDFs: companyId => `/companies/${companyId}/pdfs`,
  uploadPDF: companyId => `/companies/${companyId}/pdfs`,
  removePDF: (companyId, filename) =>
    `/companies/${companyId}/pdfs/${filename}`,
  pdfVersions: (companyId, filename) =>
    `/companies/${companyId}/pdfs/${filename}/versions`,
  pdfChangeLog: (companyId, filename) =>
    `/companies/${companyId}/pdfs/${filename}/change-log`,
  companyUsers: companyId => `/companies/${companyId}/users`,
  inviteCompanyUser: companyId => `/companies/${companyId}/users`,

  // Q&A
  askQuestion: companyId => `/companies/${companyId}/ask`,
  companyQALogs: companyId => `/companies/${companyId}/qa/logs`,

  // Vector Store
  vectorStoreStatus: companyId => `/companies/${companyId}/vector-store-status`,
  rebuildVectorStore: companyId =>
    `/companies/${companyId}/rebuild-vector-store`,
  totalVectorStoreSize: '/vector-stores/total-size',

  // Admin
  openaiModels: '/openai/models',
  updateCompanyAssistant: companyId =>
    `/admin/companies/${companyId}/assistant/update`,

  // System
  health: '/health',
  systemStatus: '/admin/system/status',

  // Chat
  chatAsk: '/chat/ask',
  chatAskStream: '/chat/ask/stream',
  chatConversation: conversationId => `/chat/conversations/${conversationId}`,
  chatConversationRetry: conversationId =>
    `/chat/conversations/${conversationId}/retry`,
  chatConversations: '/chat/conversations',
  chatExportPDF: conversationId =>
    `/chat/conversations/${conversationId}/export-pdf`,

  // Reports
  createReport: '/chat/reports',
  myReports: '/chat/reports/my',
  adminReports: '/admin/reports',
  adminReport: reportId => `/admin/reports/${reportId}`,
};

// Periodic token refresh to prevent expiration
let refreshInterval = null;

const startPeriodicRefresh = () => {
  // Clear existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  // Refresh token every 10 minutes
  refreshInterval = setInterval(
    async () => {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (token && refreshToken) {
        try {
          await refreshTokenIfNeeded();
        } catch (error) {
          console.warn('⚠️ Periodic token refresh failed:', error);
        }
      }
    },
    10 * 60 * 1000
  ); // 10 minutes
};

const stopPeriodicRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

// Start periodic refresh when module loads if user is authenticated
if (
  localStorage.getItem('access_token') &&
  localStorage.getItem('refresh_token')
) {
  startPeriodicRefresh();
}

// Export functions for manual control
export { startPeriodicRefresh, stopPeriodicRefresh };

export default api;
